import {formatDateTime, formatDuration, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {api} from "../api";
import {convertColorIntToHex, DEFAULT_CATEGORY_COLOR} from "@/shared/utils/misc";
import {ReportDriveLog} from "@/shared/timr";

// Drawn onto Leaflet map tiles, which are always light — deliberately literal.
const GPS_ACCURACY_CIRCLE_START = "#AED581";
const GPS_ACCURACY_CIRCLE_END = "#FF8A65";

declare const L: typeof import("leaflet");

export interface RecordingMapViewerConfig {
	map?: L.Map;
	fitBoundsOptions?: L.FitBoundsOptions;
}

export interface LoadTrackConfig {
	trackLoadedCallback?: (latlngs: L.LatLngExpression[], driveLog: ReportDriveLog, config: LoadTrackConfig) => void;
	trackViewCallback?: (trackData: TrackData) => void;
	fitBoundsCallback?: (trackData: TrackData) => void;
	completeCallback?: (trackData: TrackData | null) => void;
}

export interface TrackData {
	track: L.Polyline;
	startMarker: L.Marker;
	endMarker: L.Marker;
}

// Shared recording map helper: supports generic start/end markers and optional drive-log track rendering.
export class RecordingMapViewer {
	private trackData: TrackData | null = null;

	constructor(public config?: RecordingMapViewerConfig) {
	}

	public loadTrack(driveLog: ReportDriveLog, config?: LoadTrackConfig) {
		void api("reports/getTrackData.json")
			.get({driveLogId: driveLog.id})
			.done(data => {
				if (data?.length) {
					if (config?.trackLoadedCallback) {
						config.trackLoadedCallback(data, driveLog, config);
					} else {
						if (this.config?.map) {
							this.displayTrack(this.config?.map, data, driveLog, config);
						}
					}
				} else {
					if (config?.completeCallback) {
						config.completeCallback(null);
					}
				}
			});
	}

	public displayTrack(map: L.Map, latlngs: L.LatLngExpression[], driveLog: ReportDriveLog, config: LoadTrackConfig) {
		const trackColor = driveLog.category?.color ? "#" + convertColorIntToHex(driveLog.category.color) : DEFAULT_CATEGORY_COLOR;
		const track = L.polyline(latlngs, {
			color: trackColor,
			weight: 7,
			opacity: 0.7
		});
		track.addTo(map);
		const points = track.getLatLngs() as L.LatLng[];
		const startMarker = this.displayStartMarker(points[0], true, map, driveLog);
		const endMarker = this.displayEndMarker(points[points.length - 1], true, map, driveLog);
		this.trackData = {
			track: track,
			startMarker: startMarker,
			endMarker: endMarker
		};
		if (config.trackViewCallback) {
			config.trackViewCallback(this.trackData);
		}
		if (config.fitBoundsCallback) {
			config.fitBoundsCallback(this.trackData);
		} else {
			map.fitBounds(track.getBounds(), this.config?.fitBoundsOptions);
		}
		if (config.completeCallback) {
			config.completeCallback(this.trackData);
		}
	}

	public fitBounds(map: L.Map) {
		if (this.trackData?.track) {
			map.fitBounds(this.trackData.track.getBounds(), this.config?.fitBoundsOptions);
		}
	}

	public displayStartMarker(point: L.LatLng, popup = false, map?: L.Map, driveLog?: ReportDriveLog): L.Marker {
		const marker = L.marker(point, {icon: this.buildIcon("start")});
		if (popup && driveLog) {
			marker.bindPopup("" + formatDateTime(parseISOZonedDateTime(driveLog.start)) + "<br/>" +
				(driveLog.startVenue ? driveLog.startVenue + "<br/>" : "") +
				messages["report.column.car.start"] + ": " + driveLog.startMileage + "<br/>");
		}
		return marker.addTo(map || this.config?.map);
	}

	public displayIdenticalMarker(point: L.LatLng, map?: L.Map): L.Marker {
		const marker = L.marker(point, {icon: this.buildIcon("identical")});
		return marker.addTo(map || this.config?.map);
	}

	public displayStartMarkerCircle(point: L.LatLng, accuracy: number, map?: L.Map): L.Circle {
		return L.circle(point, {
			radius: accuracy,
			color: GPS_ACCURACY_CIRCLE_START
		}).addTo(map || this.config?.map);
	}

	public displayEndMarker(point: L.LatLng, popup = false, map?: L.Map, driveLog?: ReportDriveLog): L.Marker {
		const marker = L.marker(point, {icon: this.buildIcon("end")});
		if (popup && driveLog) {
			marker.bindPopup("" + formatDateTime(parseISOZonedDateTime(driveLog.end)) + "<br/>" +
				(driveLog.endVenue ? driveLog.endVenue + "<br/>" : "") +
				messages["report.column.car.end"] + ": " + driveLog.endMileage + "<br/>" +
				messages["report.column.distance"] + ": " + (driveLog.distance ?? "") + "<br/>" +
				messages["report.column.durationGross"] + ": " + formatDuration(driveLog.durationNet));
		}
		return marker.addTo(map || this.config?.map);
	}

	public displayEndMarkerCircle(point: L.LatLng, accuracy: number, identicalCircle = false, map?: L.Map): L.Circle {
		return L.circle(point, {
			radius: accuracy,
			dashArray: identicalCircle ? [30, 30] : null,
			fillOpacity: identicalCircle ? 0.07 : 0.1,
			color: GPS_ACCURACY_CIRCLE_END
		}).addTo(map || this.config?.map);
	}

	private buildIcon(marker: string): L.DivIcon {
		return L.divIcon({
			html: `<i class="fas fa-map-marker-alt gps-position-marker gps-position-${marker}-marker"></i>`,
			className: "dummy"
		});
	}
}
